import { getAdminPanelRoleDisplay } from "../../utils/adminPanelRole";
import {
  CHAT_SUPPORT_ASSIGNED_TAB_KEYS,
  readSupportHasTeam,
  readSupportTabPermissions,
} from "../../constants/supportTabPermissions";

/**
 * Simple role-aware workspace.
 *
 * My Desk (agents): Work · Targets · Performance  (≤3)
 * Team Hub (leads/admin): Today · Assign · Targets · Approvals · Performance (≤5)
 */

export const WORKSPACE_THEME = { primary: "#FB565F" };

function hasPerm(key, fallback = false) {
  const perms = readSupportTabPermissions();
  if (!perms || typeof perms !== "object") return fallback;
  if (typeof perms[key] !== "boolean") return fallback;
  return perms[key];
}

export function getWorkspaceRole() {
  return getAdminPanelRoleDisplay();
}

export function isAdminLike(role = getWorkspaceRole()) {
  const r = String(role || "").trim().toLowerCase();
  return r === "admin" || r === "developer";
}

export function canAccessAgentDesk(role = getWorkspaceRole()) {
  if (isAdminLike(role)) return true;
  if (role === "Sales" || role === "Sales Team Lead") return true;
  if (role === "CSR Team Lead") {
    const hasTeam = readSupportHasTeam();
    if (hasTeam !== true) return true;
    return (
      hasPerm("csr_outreach") ||
      hasPerm("tickets") ||
      hasPerm("chat_support_log") ||
      hasPerm("agent_reports")
    );
  }
  if (role === "CSR") {
    return hasPerm("csr_outreach") || hasPerm("tickets") || hasPerm("agent_reports");
  }
  if (role === "Chat Support") {
    return CHAT_SUPPORT_ASSIGNED_TAB_KEYS.some((k) => hasPerm(k, false));
  }
  return false;
}

export function canAccessTeamHub(role = getWorkspaceRole()) {
  if (isAdminLike(role)) return true;
  if (role === "Sales Team Lead") return true;
  if (role === "CSR Team Lead") {
    const hasTeam = readSupportHasTeam();
    if (hasTeam !== true) return true;
    return (
      hasPerm("teams") ||
      hasPerm("csr_outreach") ||
      hasPerm("chat_support_log") ||
      hasPerm("performance") ||
      hasPerm("agent_reports") ||
      hasPerm("tickets")
    );
  }
  return false;
}

/** My Desk — max 3 simple tabs, role-filtered. */
export function getAgentDeskSections(role = getWorkspaceRole()) {
  const sections = [
    {
      id: "work",
      label: "My work",
      description: "What you need today",
      component: "deskWork",
    },
    {
      id: "targets",
      label: "My targets",
      description: "Today’s progress",
      component: "deskTargets",
    },
    {
      id: "performance",
      label: "Performance",
      description: "Results overview",
      component: "deskPerformance",
    },
  ];

  // Sales agents: no daily CSR/Chat targets — keep work + performance
  if (role === "Sales" || role === "Sales Team Lead") {
    return sections.filter((s) => s.id !== "targets");
  }

  return sections;
}

/** Team Hub — few management tabs. */
export function getTeamHubSections(role = getWorkspaceRole()) {
  const sections = [
    {
      id: "today",
      label: "Today",
      description: "Both teams at a glance",
      component: "hubToday",
    },
  ];

  const canAssignCsr = isAdminLike(role) || role === "CSR Team Lead";
  const canAssignSales = isAdminLike(role) || role === "Sales Team Lead";
  if (canAssignCsr || canAssignSales) {
    sections.push({
      id: "assign",
      label: "Assign",
      description: "Give work to agents",
      component: "hubAssign",
    });
  }

  if (isAdminLike(role) || role === "CSR Team Lead") {
    sections.push({
      id: "targets",
      label: "Targets",
      description: "Set & review daily targets",
      component: "hubTargets",
    });
    sections.push({
      id: "approvals",
      label: "Approvals",
      description: "Review pending items",
      component: "approvals",
    });
  }

  // Ring bells is Admin / Developer only — not Team Lead or other roles.
  if (isAdminLike(role)) {
    sections.push({
      id: "ring-bells",
      label: "Ring bells",
      description: "Alert sounds for agents",
      component: "hubRingBells",
    });
  }

  sections.push({
    id: "performance",
    label: "Performance",
    description: "Daily & monthly results",
    component: "hubPerformance",
  });

  return sections;
}

export function getDeskWorkTools(role = getWorkspaceRole()) {
  const tools = [];

  if (role === "Chat Support" || isAdminLike(role) || role === "CSR Team Lead") {
    if (isAdminLike(role) || role === "CSR Team Lead" || hasPerm("tickets", false)) {
      tools.push({ id: "tickets", label: "Tickets", component: "support" });
    }
    if (
      isAdminLike(role) ||
      role === "CSR Team Lead" ||
      hasPerm("chat_support_log", false)
    ) {
      tools.push({ id: "log", label: "Daily log", component: "chatSupportLog" });
      tools.push({
        id: "activity",
        label: "Activity",
        component: "chatSupportActivity",
      });
    }
  }

  if (role === "CSR" || role === "CSR Team Lead" || isAdminLike(role)) {
    if (
      isAdminLike(role) ||
      role === "CSR Team Lead" ||
      hasPerm("csr_outreach", false)
    ) {
      tools.push({ id: "customers", label: "Customers", component: "csrOutreach" });
    }
  }

  if (role === "Sales" || role === "Sales Team Lead" || isAdminLike(role)) {
    tools.push({ id: "sales", label: "Sales", component: "salesCrm" });
  }

  // Pure CSR agent — only customers (not chat tools)
  if (role === "CSR") {
    return tools.filter((t) => t.id === "customers");
  }
  // Pure Chat Support — tickets / log / activity only
  if (role === "Chat Support") {
    return tools.filter((t) => ["tickets", "log", "activity"].includes(t.id));
  }
  // Sales agent / sales TL on desk — sales CRM
  if (role === "Sales" || role === "Sales Team Lead") {
    return tools.filter((t) => t.id === "sales");
  }

  return tools;
}

export function getHubAssignTools(role = getWorkspaceRole()) {
  const tools = [];
  if (isAdminLike(role) || role === "CSR Team Lead") {
    tools.push({ id: "csr", label: "CSR customers", component: "csrOutreach" });
    tools.push({ id: "leads", label: "CSR leads", component: "csrLeads" });
    tools.push({ id: "tickets", label: "Tickets", component: "support" });
    tools.push({ id: "chat", label: "Chat support", component: "chatSupportLog" });
  }
  if (isAdminLike(role) || role === "Sales Team Lead") {
    tools.push({ id: "sales", label: "Sales CRM", component: "salesCrm" });
  }
  return tools;
}
